import React from 'react';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ItemDetailsPopover } from './ItemDetailsPopover';
import { PopoverState } from '../../hooks/useItemPopover';
import { editButtonOffsetSx } from '../../App';

export interface MemberEffectItem {
  id: string;
  name: string;
  description?: string | null;
  getDescription?: () => Promise<string>;
  modifiers?: string;
  selected?: boolean;
  onClick?: () => void;
  onDelete?: () => void;
}

interface MemberEffectsBaseProps {
  effects: MemberEffectItem[];
  title?: string;
  onAdd?: () => void;
  addDisabled?: boolean;
}

export const MemberEffectsBase: React.FC<MemberEffectsBaseProps> = ({
  effects,
  title = 'Effects',
  onAdd,
  addDisabled = false,
}) => {
  const [popover, setPopover] = React.useState<PopoverState | null>(null);

  const handleChipClick = async (event: React.MouseEvent<HTMLElement>, effect: MemberEffectItem) => {
    effect.onClick?.();
    if (effect.description == null && !effect.getDescription && !effect.modifiers) return;
    setPopover({
      anchor: event.currentTarget,
      name: effect.name,
      description: effect.getDescription ? 'Loading…' : effect.description ?? null,
      modifiers: effect.modifiers,
      tags: [],
    });
    if (effect.getDescription) {
      const description = await effect.getDescription();
      setPopover((prev) => (prev ? { ...prev, description: description || null } : null));
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
        {onAdd && <IconButton sx={editButtonOffsetSx} disabled={addDisabled} size="small" onClick={onAdd}><AddIcon /></IconButton>}
      </Stack>
      {effects.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
          {effects.map((effect) => (
            <Chip
              key={effect.id}
              size="small"
              label={effect.name}
              color={effect.selected ? 'primary' : 'default'}
              onClick={(event) => handleChipClick(event, effect)}
              onDelete={effect.onDelete}
            />
          ))}
        </Box>
      )}
      <ItemDetailsPopover popover={popover} onClose={() => setPopover(null)} />
    </Box>
  );
};